import { Box, Button, Card, CardContent, Link, TableCell, TextField, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useState } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  BaseDatePicker,
  BaseHeaderTitle,
  BaseImage,
  BaseRow,
  BaseScrollbar,
  BaseSelect,
  BaseTable,
  BaseUploadFile,
} from '~/components/common';
import BaseCalendar from '~/components/common/base-calendar/BaseCalendar';
import { OptionUploadFile, SelectOption } from '~/utils/types/common';
import * as IconComponents from '~/assets/icons';
import UploadFileDialog from '~/components/common/upload-file-dialog/UploadFileDialog';

const iconComponentList: Array<{
  name: string;
  component: React.FunctionComponent<React.SVGProps<SVGSVGElement> & { title?: string }>;
}> = [];

Object.keys(IconComponents).forEach((name) => {
  iconComponentList.push({ name, component: IconComponents[name as keyof typeof IconComponents] });
});

const Component = () => {
  //#region BaseCalendar
  const [events] = useState([]);
  const [open, setOpen] = useState(false);
  const optionUploadFile: OptionUploadFile = {
    uploadFile: true,
    selectFromProject: true,
  };
  //#endregion BaseCalendar

  //#region BaseDatePicker
  const [baseDatePicker, setBaseDatePicker] = useState(dayjs().toDate());
  //#endregion BaseDatePicker

  //#region BaseSelect
  const [baseSelect, setBaseSelectValue] = useState();
  const [baseSelectOptions] = useState<Array<SelectOption>>([
    { id: '1', name: 'Option 1' },
    { id: '2', name: 'Option 2' },
  ]);
  //#endregion BaseSelect

  return (
    <Box className='w-full h-full'>
      {/* Icons */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<Icon />'}</Typography>
          <Box className='flex flex-wrap gap-[10px]'>
            {iconComponentList.map((iconComponent) => {
              const Component = iconComponent.component;
              return (
                <Box
                  className='bg-[#DDDDDD] p-[10px] flex flex-col justify-center items-center w-[150px] h-[100px]'
                  key={iconComponent.name}
                >
                  <Component className='w-[50px] h-[50px] mb-[10px]' />
                  <Box className='w-full truncate text-center'>{iconComponent.name}</Box>
                </Box>
              );
            })}
          </Box>
        </CardContent>
      </Card>

      {/* BaseHeaderTitle */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseHeaderTitle />'}</Typography>
          <Box className='mb-[10px]'>Page title</Box>
          <BaseHeaderTitle title='Component List' />
        </CardContent>
      </Card>
      {/* BaseCalendar */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseCalendar />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://jquense.github.io/react-big-calendar/examples/index.html'>
              {' https://jquense.github.io/react-big-calendar/examples/index.html'}
            </Link>
          </Box>
          <BaseCalendar events={events} style={{ height: '300px' }} />
        </CardContent>
      </Card>
      {/* BaseDatePicker */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseDatePicker />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://reactdatepicker.com'>{' https://reactdatepicker.com'}</Link>
          </Box>
          <BaseDatePicker
            value={baseDatePicker}
            onChange={(value) => {
              if (value) setBaseDatePicker(value);
            }}
          />
        </CardContent>
      </Card>
      {/* BaseImage */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseImage />'}</Typography>
          <Box className='mb-[10px]'>Img element with default image</Box>
          <BaseImage />
        </CardContent>
      </Card>
      {/* BaseRow */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseRow />'}</Typography>
          <Box className='mb-[10px]'>Row with responsive</Box>
          <BaseRow>
            <Box className='border p-2 mr-2'>Column 1</Box>
            <Box className='border p-2'>Column 2</Box>
          </BaseRow>
        </CardContent>
      </Card>
      {/* BaseScrollbar */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseScrollbar />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://grsmto.github.io/simplebar'>{' https://grsmto.github.io/simplebar'}</Link>
          </Box>
          <BaseScrollbar maxHeight='50px'>
            <Box className='border'>
              <Box>Scrollbar content</Box>
              <Box>Scrollbar content</Box>
              <Box>Scrollbar content</Box>
              <Box>Scrollbar content</Box>
              <Box>Scrollbar content</Box>
            </Box>
          </BaseScrollbar>
        </CardContent>
      </Card>
      {/* BaseSelect */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseSelect />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://mui.com/material-ui/react-select'>{' https://mui.com/material-ui/react-select'}</Link>
          </Box>
          <BaseSelect
            value={baseSelect}
            options={baseSelectOptions}
            onChange={(e) => setBaseSelectValue(e.target.value)}
          />
        </CardContent>
      </Card>
      {/* BaseTable */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseTable />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://mui.com/material-ui/react-table'>{' https://mui.com/material-ui/react-table'}</Link>
          </Box>
          <BaseTable<{ id: string; name: string }>
            data={[{ id: '1', name: 'Name' }]}
            headCells={[
              {
                id: 'id',
                label: 'ID',
                sortable: false,
                searchable: false,
              },
              {
                id: 'name',
                label: 'Name',
                sortable: false,
                searchable: false,
              },
            ]}
            setSelectedIds={() => {}}
            page={1}
            totalPages={10}
            renderCell={(item: { id: string; name: string }) => {
              return (
                <>
                  <TableCell component='th' scope='row' padding='none'>
                    <Box>{item.id}</Box>
                  </TableCell>
                  <TableCell component='th' scope='row' padding='none'>
                    <Box>{item.name}</Box>
                  </TableCell>
                </>
              );
            }}
          />
        </CardContent>
      </Card>
      {/* BaseUploadFile */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<BaseUploadFile />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://react-dropzone.js.org'>{' https://react-dropzone.js.org'}</Link>
          </Box>
          <BaseUploadFile />
        </CardContent>
      </Card>
      {/* Form */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<Formik />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://formik.org'>{' https://formik.org'}</Link>
          </Box>
        </CardContent>
      </Card>
      {/* Datetime */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'Datetime: dayjs'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://day.js.org'>{' https://day.js.org'}</Link>
          </Box>
        </CardContent>
      </Card>
      {/* Drag and Drop */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'Drag and Drop'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://dndkit.com'>{' https://dndkit.com'}</Link>
          </Box>
        </CardContent>
      </Card>
      {/* Number format */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<NumericFormat />'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://s-yadav.github.io/react-number-format/docs/props'>
              {' https://s-yadav.github.io/react-number-format/docs/props'}
            </Link>
          </Box>
          <NumericFormat customInput={TextField} value='1000' thousandSeparator={true} prefix={'$'} decimalScale={2} />
        </CardContent>
      </Card>
      {/* notify() */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'notify("success", "Message")'}</Typography>
          <Box className='mb-[10px]'>
            Show notification. Read more:
            <Link href='https://fkhadra.github.io/react-toastify/introduction'>
              {' https://fkhadra.github.io/react-toastify/introduction'}
            </Link>
          </Box>
        </CardContent>
      </Card>
      {/* Translation */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'Translation: I18next'}</Typography>
          <Box className='mb-[10px]'>
            Read more:
            <Link href='https://www.i18next.com'>{' https://www.i18next.com'}</Link>
          </Box>
        </CardContent>
      </Card>

      {/* upload file dialog */}
      <Card className='mb-[30px]'>
        <CardContent>
          <Typography variant='h5'>{'<UploadFileDialog />'}</Typography>
          <Button color='primary' variant='contained' onClick={() => setOpen(true)}>
            Upload File Dialog
          </Button>
          <UploadFileDialog
            open={open}
            setOpen={(value: boolean) => setOpen(value)}
            optionUploadFile={optionUploadFile}
          />
        </CardContent>
      </Card>
    </Box>
  );
};

export default Component;
